import { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X, Play } from "lucide-react";
import { LazyImg, LazyVideo } from "@/components/LazyMedia";

export default function ProductGallery({ images, video, overlay, activeSrc }) {
  const media = [
    ...(video ? [{ type: "video", src: video }] : []),
    ...((images || []).map((src) => ({ type: "image", src }))),
  ];
  const [idx, setIdx] = useState(0);
  const [modal, setModal] = useState(false);
  const touchX = useRef(null);
  const cur = media[idx];

  useEffect(() => {
    if (!activeSrc) return;
    const i = media.findIndex((m) => m.type === "image" && m.src === activeSrc);
    if (i >= 0) setIdx(i);
  }, [activeSrc]); // eslint-disable-line

  const go = (d) => setIdx((i) => (i + d + media.length) % media.length);
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40 && media.length > 1) { go(dx < 0 ? 1 : -1); }
    touchX.current = null;
  };

  useEffect(() => {
    if (!modal) return;
    const onKey = (e) => { if (e.key === "Escape") setModal(false); if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modal]); // eslint-disable-line

  if (!media.length) return <div className="aspect-square rounded-2xl border border-slate-200 bg-slate-50 flex items-center justify-center text-slate-300">Tanpa gambar</div>;

  return (
    <div className="lg:sticky lg:top-24 self-start" data-testid="product-gallery">
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="aspect-square bg-slate-50 relative overflow-hidden select-none touch-pan-y"
          onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          {overlay}
          {cur.type === "video" ? (
            <LazyVideo key={cur.src} src={cur.src} controls playsInline data-testid="gallery-video"
              className="w-full h-full object-contain bg-black" />
          ) : (
            <LazyImg key={cur.src} src={cur.src} eager={idx === 0} data-testid="gallery-main-image" onClick={() => setModal(true)} draggable={false}
              className="w-full h-full object-contain p-6 cursor-zoom-in" />
          )}
          {media.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); go(-1); }} data-testid="gallery-prev"
                className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-slate-50 active:scale-95 transition z-10"><ChevronLeft className="w-5 h-5 text-[#00133F]" /></button>
              <button onClick={(e) => { e.stopPropagation(); go(1); }} data-testid="gallery-next"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-slate-50 active:scale-95 transition z-10"><ChevronRight className="w-5 h-5 text-[#00133F]" /></button>
            </>
          )}
        </div>
      </div>
      {media.length > 1 && (
        <div className="flex gap-2 mt-3 flex-wrap">
          {media.map((m, i) => (
            <button key={i} onClick={() => setIdx(i)} data-testid={`gallery-thumb-${i}`}
              className={`relative w-16 h-16 rounded-xl overflow-hidden border-2 ${idx === i ? "border-[#F0005A]" : "border-slate-200"}`}>
              {m.type === "video" ? (
                <><LazyVideo src={`${m.src}#t=0.1`} className="w-full h-full object-cover bg-black" muted />
                  <span className="absolute inset-0 flex items-center justify-center bg-black/30"><Play className="w-5 h-5 text-white fill-white" /></span></>
              ) : <LazyImg src={m.src} className="w-full h-full object-cover" />}
            </button>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-400 mt-2 text-center lg:text-left">Klik gambar untuk tampilan penuh.</p>

      {modal && createPortal(
        <div className="fixed inset-0 z-[90] bg-black/85 flex items-center justify-center p-4" data-testid="gallery-modal" onClick={() => setModal(false)}>
          <button className="absolute top-4 right-4 text-white/80 hover:text-white z-10" data-testid="gallery-modal-close" onClick={() => setModal(false)}><X className="w-7 h-7" /></button>
          {media.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); go(-1); }} data-testid="gallery-modal-prev" className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center z-10"><ChevronLeft className="w-6 h-6" /></button>
              <button onClick={(e) => { e.stopPropagation(); go(1); }} data-testid="gallery-modal-next" className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center z-10"><ChevronRight className="w-6 h-6" /></button>
            </>
          )}
          <div className="flex items-center gap-4 max-w-6xl w-full h-full justify-center" onClick={(e) => e.stopPropagation()}>
            <div className="flex-1 h-full flex items-center justify-center">
              {cur.type === "video"
                ? <video src={cur.src} controls autoPlay playsInline className="max-h-[85vh] max-w-full object-contain rounded-lg bg-black" data-testid="gallery-modal-video" />
                : <img src={cur.src} alt="" className="max-h-[85vh] max-w-full object-contain rounded-lg" data-testid="gallery-modal-image" />}
            </div>
            <div className="hidden sm:flex flex-col gap-2 max-h-[85vh] overflow-y-auto w-20 shrink-0">
              {media.map((m, i) => (
                <button key={i} onClick={() => setIdx(i)} data-testid={`gallery-modal-thumb-${i}`}
                  className={`relative w-16 h-16 rounded-lg overflow-hidden border-2 ${idx === i ? "border-[#F0005A]" : "border-white/30"}`}>
                  {m.type === "video" ? (
                    <><video src={`${m.src}#t=0.1`} preload="metadata" className="w-full h-full object-cover bg-black" muted /><span className="absolute inset-0 flex items-center justify-center bg-black/30"><Play className="w-4 h-4 text-white fill-white" /></span></>
                  ) : <img src={m.src} alt="" loading="lazy" className="w-full h-full object-cover" />}
                </button>
              ))}
            </div>
          </div>
        </div>, document.body)}
    </div>
  );
}
